import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Coins, HelpCircle, Loader2, Maximize2 } from "lucide-react";
import { useRouter } from "next/router";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { calculateModelCost } from "@/utils/goldCostUtils";
import { Modal } from "components/atoms/Modal/index";
import { editImage, getUpscalerModelConfig, poll, pollEditImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, EditType, ImageDTO, IModelConfig, UserPremiumType } from "types";
import ModelSelection from "../ModelSelection";
interface UpscalerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (editRequest: string, isPremium: boolean, editType: string, goldCost: number, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string, creativity?: number, model?: string) => void;
  imageId: string;
  image?: ImageDTO;
}
export const UpscalerModal: FC<UpscalerModalProps> = ({
  isOpen,
  onClose,
  imageId,
  onSubmit,
  image
}) => {
  const router = useRouter();
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [upscaleSize, setUpscaleSize] = useState(2.0);
  const [creativity, setCreativity] = useState(0.35);
  const [modelConfigs, setModelConfigs] = useState<IModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState<IModelConfig | null>(null);
  const [imageWidth, setImageWidth] = useState<number>(1024);
  const [imageHeight, setImageHeight] = useState<number>(1024);
  const maxDimension = 4096;

  // Mock image dimensions - in a real app, these would come from props
  const finalWidth = imageWidth * upscaleSize;
  const finalHeight = imageHeight * upscaleSize;
  const maxUpscaleFactor = Math.round(Math.min(Math.min(maxDimension / imageWidth, maxDimension / imageHeight), 4));
  const totalGoldCost = useMemo(() => {
    // If clarity upscaler is selected or no model selected, compute cost from final dimension
    if (!selectedModel || selectedModel?.modelName === "Clarity Upscaler") {
      return Math.ceil(upscaleSize * 6);
    }

    // Otherwise, use model-based cost
    return calculateModelCost(selectedModel.modelName, UserPremiumType.NONE);
  }, [selectedModel, upscaleSize]);
  useEffect(() => {
    if (selectedModel && selectedModel?.modelName !== "Clarity Upscaler") {
      if (imageWidth > imageHeight) {
        setUpscaleSize(4096 / imageWidth);
      } else {
        setUpscaleSize(4096 / imageHeight);
      }
    } else if (selectedModel?.modelName === "Clarity Upscaler" && upscaleSize > 4) {
      setUpscaleSize(2);
    }
  }, [imageHeight, imageWidth, selectedModel, upscaleSize]);

  // On mount or when media changes, load actual dimensions
  useEffect(() => {
    if (image) {
      const img = new Image();
      img.src = image.image;
      img.onload = () => {
        setImageWidth(img.naturalWidth);
        setImageHeight(img.naturalHeight);
      };
    }
  }, [image]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getUpscalerModelConfig();
        setModelConfigs(data.modelConfigs);
        const defaultConfig = data.modelConfigs.find(cfg => cfg.modelName === "Clarity Upscaler") || data.modelConfigs[0];
        setSelectedModel(defaultConfig);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, []);
  const handleEditSubmit = useCallback(async (imageId: string, editType: string, scale: number) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      toast.error("You need to be a subscribed user to use this feature.");
      return;
    }
    if (state.gold < scale * 4) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("imageId", imageId);
    formData.append("editType", editType);
    formData.append("cfgScale", scale.toString());
    if (selectedModel) {
      formData.append("model", selectedModel.modelName);
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    if (error) {
      toast.error("An error occurred while submitting the edit request.");
      return;
    }
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => {
        const result = await pollEditImage(requestId);
        if (result.error && typeof result.error === "object") {
          console.log("Error details:", result.error);
        }
        return result;
      }, ({
        image,
        error
      }) => {
        // Continue polling only if there is no image and no critical error
        if (!!image) return false; // Stop polling when an image is retrieved
        if (typeof error === "string") return false;
        if (typeof error === "object") return false;
        return true; // Continue polling otherwise
      }, 5000);
      if (error) {
        if (typeof error === "object") {
          toast.error(error.message || "An unknown error occurred while generating the image and your gold has been refunded. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + scale * 4
            }
          });
        } else if (typeof error === "string") {
          toast.error("The request timed out. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + scale * 4
            }
          });
        }
      }
      if (image && image.length > 0) {
        const newImage = Array.isArray(image) ? image[0] : image;
        router.push(`/images/${newImage.id}`);
        dispatch({
          type: AppReducerActionType.SET_GOLD,
          payload: {
            ...state,
            gold: state.gold - scale * 4
          }
        });
      }
    }
    setLoading(false);
  }, [state, selectedModel, dispatch, router]);
  const handleSubmit = async () => {
    if (onSubmit) {
      await onSubmit("Upscale Image", false, EditType.UPSCALER, totalGoldCost, undefined, upscaleSize, undefined, undefined, undefined, selectedModel?.modelName === "Clarity Upscaler" ? creativity : undefined, selectedModel?.modelName);
    } else {
      await handleEditSubmit(imageId, EditType.UPSCALER, upscaleSize);
    }
    onClose();
  };
  const handleClose = () => {
    setLoading(false);
    setUpscaleSize(2.0);
    setCreativity(0.35);
    onClose();
  };
  const renderField = (label: string, tooltip: string, children: React.ReactNode) => <div className="space-y-2" data-sentry-component="renderField" data-sentry-source-file="index.tsx">
            <div className="flex items-center gap-2">
                <Label data-sentry-element="Label" data-sentry-source-file="index.tsx">{label}</Label>
                <Popover data-sentry-element="Popover" data-sentry-source-file="index.tsx">
                    <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="index.tsx">
                        <Button variant="ghost" className="h-4 w-4 p-0 hover:bg-transparent" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            <HelpCircle className="h-4 w-4 text-muted-foreground" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 bg-modal-background border-modal-border" data-sentry-element="PopoverContent" data-sentry-source-file="index.tsx">
                        <p className="text-sm text-modal-text">{tooltip}</p>
                    </PopoverContent>
                </Popover>
            </div>
            {children}
        </div>;
  return <Modal open={isOpen} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-component="UpscalerModal" data-sentry-source-file="index.tsx">
            <div className="relative mx-4 my-4 rounded-lg p-6 shadow-xl overflow-y-auto max-h-[calc(90vh-2rem)] max-w-4xl space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center text-white">
                    Upscaling
                </h2>
                <div>
                    <p className="text-gray-400">
                        Enhance the Image Resolution adding Detail and Clarity
                        to your Image while Resizing it.
                    </p>
                </div>

                <div className="h-px bg-modal-border" />

                <div className="space-y-4">
                    <div className="w-fit mx-auto bg-gradient-to-r from-pink-600 to-purple-600 text-[10px] text-white px-2 py-1 rounded-full shadow-lg animate-pulse">
                        New Models!!
                    </div>
                    <ModelSelection modelConfigs={modelConfigs} onConfigChange={() => {}} // No need to handle config change
        selectedModel={selectedModel} setSelectedModel={setSelectedModel} data-sentry-element="ModelSelection" data-sentry-source-file="index.tsx" />
                    <span className="text-[#F0B853] text-xs">
                        Cost:{" "}
                        {calculateModelCost(selectedModel?.modelName, state.userPremiumLevel)}{" "}
                        gold
                    </span>
                </div>
                <div className="space-y-4">
                    {selectedModel?.modelName === "Clarity Upscaler" && <>
                            {renderField("Upscale Factor", "The upscale factor determines the size of the output image.", <div className="flex flex-col">
                                    <div className="flex justify-between items-center">
                                        <Slider id="upscale-factor" min={1} max={maxUpscaleFactor} itemType="number" step={0.5} value={[upscaleSize]} onValueChange={value => setUpscaleSize(value[0])} className="w-full" />
                                        <span>
                                            Upscale Factor: {upscaleSize}x
                                        </span>
                                    </div>
                                    {selectedModel?.modelName === "Clarity Upscaler" && <p className="text-yellow-400 text-sm">
                                            Gold Cost is based on a factor of
                                            the final image resolution.
                                        </p>}
                                </div>)}
                            {renderField("Creativity", "The creativity parameter controls the amount of randomness in the output image.", <div className="flex justify-between items-center">
                                    <Slider id="creativity" min={0} max={1} step={0.01} value={[creativity]} onValueChange={value => setCreativity(value[0])} className="w-full" />
                                    <span>
                                        Creativity: {creativity.toFixed(2)}{" "}
                                    </span>
                                </div>)}
                        </>}

                    <div className="flex flex-col gap-2">
                        <div className="text-sm text-gray-300">
                            Original: {imageWidth}×{imageHeight}
                        </div>
                        <div className="text-sm text-gray-300">
                            Final: {finalWidth.toFixed(0)}×
                            {finalHeight.toFixed(0)}
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400">
                            <Coins className="h-4 w-4" data-sentry-element="Coins" data-sentry-source-file="index.tsx" />
                            <span>Cost: {totalGoldCost} Gold</span>
                        </div>
                        <div className="flex justify-end md:space-x-4 space-y-2 flex-col md:flex-row">
                            <Button variant="outline" onClick={handleClose} className="border-modal-border hover:bg-modal-border hover:text-white w-full" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                {loading ? <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Processing...
                                    </> : <>
                                        <Maximize2 className="mr-2 h-4 w-4" />
                                        Upscale Image
                                    </>}
                            </Button>
                        </div>

                        <p className="text-orange-400 text-sm text-center">
                            Please note: The gold cost has been updated recently
                            due to a mis-pricing.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>;
};